import React, { FormEvent, useContext, useState, useEffect, JSX } from 'react';
import { WorkflowContext } from "../../context/WorkflowContextProvider";
import Form from '@rjsf/mui';
import { IChangeEvent } from '@rjsf/core';
import { RJSFSchema, UiSchema, RegistryWidgetsType } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import {useReactFlow, applyNodeChanges, ReactFlowInstance} from "@xyflow/react";
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { NodeDataStoreSelectWidget } from "./components/NodeDataStoreSelectWidget";
import { NodeDataStoreMultiSelectWidget } from "./components/NodeDataStoreMultiSelectWidget";
import { DefaultFormDialog  } from "./components/DefaultFormDialog";
import { processNodeDelete, processNodeFormSubmit, formSchema, nodeDescription } from "./functions/workflow-summarize-node-configuration";
import {
    Button
} from '@mui/material';

const defaultUiSchema: UiSchema = {
    'data_inputs': {
        items: {
            'data_input': {
                'ui:widget': 'NodeDataStoreSelectWidget',
                'ui:options': {
                    'classNames': 'data-management',
                    enumDisabled: [
                        'hq_params',
                        'data_to_loop',
                        'messages',
                        'scraper_pending'
                    ]
                }
            },
        }
    },

    'data_management': {
        'ui:widget': 'NodeDataStoreSelectWidget',
        'ui:options': {
            'classNames': 'data-management',
            enumDisabled: [
                'hq_params',
                'data_to_loop',
                'latest_user_message',
                'scraper_pending'
            ]
        }
    },
    'data_input': {
        'ui:widget': 'NodeDataStoreSelectWidget',
        'ui:options': {
            'classNames': 'data-management',
            enumDisabled: [
                'hq_params',
                'data_to_loop',
                'messages',
                'scraper_pending'
            ]
        }
    },
    'ui:order': [
        'data_input',
        'input_key',
        'data_type',
        'process_method',
        'allOf',
        'string_process',
        'process_type',
        'data_inputs',
        'add_number',
        'subtract_number',
        'multiply_number',
        'divide_number',
        'replace_text',
        'replace_with_text',
        'separator',
        'array_key',
        'model_key',
        'data_management'
    ]
}

export const WorkflowSummarizeNodeConfiguration: () => JSX.Element = (): JSX.Element => {
    const context: any = useContext(WorkflowContext);
    const reactFlowInstance: ReactFlowInstance = useReactFlow();
    const [formData, setFormData] = useState<any>();
    const [uiSchema, setUiSchema] = useState<UiSchema>(defaultUiSchema);

    function handleCloseDialog(): void {
        context.toggleShowSummarizeModal(false);
    }

    const handleFormSubmit: (data: IChangeEvent, event: FormEvent<any>) => void = (data: IChangeEvent, event: FormEvent<any>) : void => {
        console.log(data.formData);
        processNodeFormSubmit(context, data.formData, reactFlowInstance);
        handleCloseDialog();
    }

    function deleteNode() {
        processNodeDelete(context, reactFlowInstance);
        handleCloseDialog();
    }

    function handleDataChange(data: any) : void {
        let schema: UiSchema = uiSchema;
        switch (data.data_type) {
            case 'Text':
                const textDisable: string[] = [
                    'Count',
                    'Explode to Text (Comma Separated Values)',
                    'Max',
                    'Min',
                    'Sum',
                    'Average',
                    'Add Number',
                    'Subtract Number',
                    'Multiply Number',
                    'Divide Number',
                ];
                if (textDisable.includes(data.process_method)) {
                    data.process_method = 'Capitalize';
                }

                schema.process_method = {
                    'ui:options': {
                        enumDisabled: textDisable
                    }
                }
                break;
            case 'Array':
                const arrayDisable: string[] = [
                    'All Lowercase',
                    'All Uppercase',
                    'Capitalize',
                    'Concatenate',
                    'Text Length',
                    'Replace with Text',
                    'Max',
                    'Min',
                    'Sum',
                    'Average',
                    'Add Number',
                    'Subtract Number',
                    'Multiply Number',
                    'Divide Number',
                ];

                if (arrayDisable.includes(data.process_method)) {
                    data.process_method = 'Count';
                }

                schema.process_method = {
                    'ui:options': {
                        enumDisabled: arrayDisable
                    }
                }
                break;

            case 'Number':
                const numberDisable: string[] = [
                    'All Lowercase',
                    'All Uppercase',
                    'Capitalize',
                    'Concatenate',
                    'Text Length',
                    'Replace with Text',
                    'Count',
                    'Explode to Text (Comma Separated Values)',
                ];

                if (numberDisable.includes(data.process_method)) {
                    data.process_method = 'Max';
                }

                schema.process_method = {
                    'ui:options': {
                        enumDisabled: numberDisable
                    }
                }
                break;
        }
        setFormData(data);
        setUiSchema(schema);

    }


    const widgets: RegistryWidgetsType = {
        NodeDataStoreSelectWidget: NodeDataStoreSelectWidget,
        NodeDataStoreMultiSelectWidget: NodeDataStoreMultiSelectWidget
    }

    useEffect( (): void => {
        if (context.activeEditSummarizeNode !== undefined && context.activeEditSummarizeNode.data !== undefined) {
            setFormData(context.activeEditSummarizeNode.data);
        }
    } , [context] );

    return (
        <DefaultFormDialog
            open={ context.showSummarizeModal }
            title={ "Summarize Node" }
            handleClose={ handleCloseDialog }
            description={ nodeDescription }
        >
            <span style={ { paddingLeft: '15px', fontWeight: 'bold' } }>{ "Node ID: " + context?.activeEditSummarizeNode?.id }</span>
            <Form
                validator={validator}
                schema={ formSchema }
                uiSchema={ uiSchema }
                onSubmit={ handleFormSubmit }
                widgets={ widgets }
                id="workflow-summarize-node-configure"
                formData={ formData }
                formContext={ formData }
                onChange={({ formData }) => handleDataChange(formData) }
            >
                <div className="dialog__actions form-group">
                    <Button type="submit"
                            variant="contained"
                            color="success"
                    >
                        <SaveIcon />
                        <span>{ "Save" }</span>
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={ deleteNode }
                        type="button">
                        <DeleteIcon />
                        <span>{ "Delete" }</span>
                    </Button>

                </div>
            </Form>
        </DefaultFormDialog>
    );

}